import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Mail, Phone, MapPin, ExternalLink } from 'lucide-react'
import broxelLogo from '../../assets/images/BROXEL-LOGO.png'
import { USEFUL_LINKS } from '../../lib/externalLinks'
import SocialMediaLinks from '../ui/SocialMediaLinks'
import BrandLockup from '../brand/BrandLockup'
import { useBrand } from '../../lib/brand'
import { OFFICES, CONTACT_EMAILS } from '../../lib/contactInfo'
import { localizeDigits } from '../../lib/arabicDigits'

// Pinned to Cairo so the year does not roll early or late for an overseas
// visitor — the same reasoning as emailService.defaultVariables() on the
// backend, which reads {{year}} in Cairo time for the same footer line in
// email templates.
const currentCairoYear = () =>
  new Intl.DateTimeFormat('en-US', { timeZone: 'Africa/Cairo', year: 'numeric' }).format(new Date())

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language as 'ar' | 'en'
  const brand = useBrand()

  return (
    <footer className="bg-gradient-to-r from-muted/30 to-muted/60 border-t border-border/40">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center space-x-3 group">
              {brand === 'pharma4link' ? (
                <BrandLockup width={112} className="transition-opacity group-hover:opacity-80" />
              ) : (
                <img
                  src={broxelLogo}
                  alt="Broxel Pharmaceuticals"
                  className="h-12 w-auto transition-opacity group-hover:opacity-80"
                />
              )}
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              {t('footer.description')}
            </p>
            <SocialMediaLinks variant="default" size="md" className="pt-2" wechat="modal" />
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-6 text-lg relative">
              {t('footer.quickLinks')}
              <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-primary rounded-full"></div>
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/" 
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('nav.home')}
                </Link>
              </li>
              {/* Parked 2026-08-29 along with the /services routes in App.tsx.
              <li>
                <Link
                  to="/services"
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.services')}
                </Link>
              </li>
              */}
              <li>
                <Link
                  to="/eqs"
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.eqs')}
                </Link>
              </li>
              {/* Parked 2026-08-07 along with the /partners route in App.tsx.
              <li>
                <Link
                  to="/partners"
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.partners')}
                </Link>
              </li>
              */}
              {/* <li>
                <Link
                  to="/marketplace"
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.equipment')}
                </Link>
              </li> */}
              <li>
                <Link
                  to="/about"
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.about')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-block"
                >
                  {t('footer.contact')}
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Useful Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-6 text-lg relative">
              {t('footer.usefulLinks')}
              <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-primary rounded-full"></div>
            </h3>
            <ul className="space-y-2">
              {USEFUL_LINKS[currentLanguage].map((link, index) => (
                <li key={index}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground hover:text-primary hover:translate-x-1 transition-all duration-200 inline-flex items-center gap-1.5 group"
                  >
                    <span>{link.name}</span>
                    <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-foreground mb-6 text-lg relative">
              {t('footer.contactInfo')}
              <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-primary rounded-full"></div>
            </h3>
            <ul className="space-y-5">
              {OFFICES.map((office) => (
                <li key={office.id} className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
                    {t(office.nameKey)}
                  </p>
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span className="text-sm text-muted-foreground">{t(office.addressKey)}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="h-4 w-4 text-primary shrink-0" />
                    <a
                      href={`tel:${office.tel}`}
                      dir="ltr"
                      className="text-sm text-muted-foreground transition-colors hover:text-primary"
                    >
                      {localizeDigits(office.telDisplay, currentLanguage)}
                    </a>
                  </div>
                </li>
              ))}
              {/* One row per address: both are live, neither replaced the
                  other. The icon repeats rather than heading a stacked pair so
                  each row matches the phone rows above it. */}
              {CONTACT_EMAILS.map((email) => (
                <li key={email} className="flex items-center space-x-3 group">
                  <Mail className="h-5 w-5 text-primary shrink-0" />
                  <a
                    href={`mailto:${email}`}
                    className="text-sm text-muted-foreground group-hover:text-primary transition-colors break-all"
                  >
                    {email}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-border/40 mt-16 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-muted-foreground">
              © {currentCairoYear()} Broxel Pharmaceuticals. {t('footer.rights')}.
            </p>
            <div className="flex space-x-8 mt-4 md:mt-0">
              <Link
                to="/privacy"
                className="text-sm text-muted-foreground hover:text-primary transition-colors relative group"
              >
                {t('footer.privacyPolicy')}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link
                to="/terms"
                className="text-sm text-muted-foreground hover:text-primary transition-colors relative group"
              >
                {t('footer.termsOfService')}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
